import React, { useState, useCallback, useContext, createContext } from 'react';
import api from 'services';
import { useFazenda } from 'hooks/providers/cadastros/fazendas';
import { toPriceInt } from 'utils/converters';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

const FolhaMensalistaContext = createContext({});

const folhaList = [
  {
    id: 'mensalista',
    descricao: 'Mão de Obra',
  },
  {
    id: 'colheita',
    descricao: 'Colheita',
  },
];

const pagamentoList = [
  {
    id: 1,
    descricao: 'Pagamento Único',
  },
  {
    id: 2,
    descricao: 'Pagamento Parcelado',
  },
];

const extrairNomesColaboradores = (mensagem) => {
  const nomes = [];

  const matchUnico = mensagem.match(/colaborador mensalista ['"]([^'"]+)['"]/i);
  if (matchUnico) {
    nomes.push(matchUnico[1]);
    return nomes;
  }

  const matchMultiplos = mensagem.match(
    /colaboradores mensalistas[^:]+:\s*([^.]+)/i,
  );
  if (matchMultiplos) {
    const listaNomes = matchMultiplos[1].split(',').map((nome) => nome.trim());
    return listaNomes.filter((nome) => nome.length > 0);
  }

  return nomes;
};

const buscarColaboradoresPorNome = (nomes, detalhesFolha) => {
  if (!detalhesFolha || !Array.isArray(detalhesFolha) || nomes.length === 0) {
    return nomes.map((nome) => ({ nome, id: null }));
  }

  return nomes.map((nome) => {
    const colaborador = detalhesFolha.find((item) => {
      const nomeItem = item.nome || item.colaborador?.nome || '';
      return (
        nomeItem.toUpperCase().includes(nome.toUpperCase()) ||
        nome.toUpperCase().includes(nomeItem.toUpperCase())
      );
    });

    const colaboradorId =
      colaborador?.glo_vinculo_id ||
      colaborador?.colaborador?.id ||
      colaborador?.id ||
      null;

    return {
      nome,
      id: colaboradorId,
    };
  });
};

export function FolhaMensalistaProvider({ children }) {
  const { showToast } = useNewToast();

  const [folhaDetails, setFolhaDetails] = useState([]);
  const [folhaDetailsLoading, setFolhaDetailsLoading] = useState(false);

  const [folhasMensalista, setFolhasMensalista] = useState([]);
  const [folhasMensalistaLoading, setFolhasMensalistaLoading] = useState(false);
  const [folhaMensalista, setFolhaMensalista] = useState({});
  const [folhaMensalistaLoading, setFolhaMensalistaLoading] = useState(false);

  const [insertFolhaLoading, setInsertFolhaLoading] = useState(false);
  const [changeFolhaLoading, setChangeFolhaLoading] = useState(false);
  const [deleteFolhaLoading, setDeleteFolhaLoading] = useState(false);
  const [fecharFolhaMensalistaLoading, setFecharFolhaMensalistaLoading] =
    useState(false);
  const [abrirFolhaMensalistaLoading, setAbrirFolhaMensalistaLoading] =
    useState(false);
  const [setPagamentoMensalistaLoading, setSetPagamentoMensalistaLoading] =
    useState(false);

  const [folhasMensalistaLimite, setFolhasMensalistaLimite] = useState({
    content: [],
    pagination: {},
  });
  const [folhasMensalistaLimiteLoading, setFolhasMensalistaLimiteLoading] =
    useState(false);

  const [employeesWithoutSalaryError, setEmployeesWithoutSalaryError] =
    useState({
      open: false,
      mensagem: '',
      colaboradores: [],
    });

  const [
    downloadFolhaMensalistaXlsxLoading,
    setDownloadFolhaMensalistaXlsxLoading,
  ] = useState(false);

  const [downloadResumoXlsxLoading, setDownloadResumoXlsxLoading] =
    useState(false);

  const { fazendaAtiva } = useFazenda();

  const getFolhasMensalista = useCallback(async (filter) => {
    try {
      setFolhasMensalistaLoading(true);
      const response = await api.folhaMensalista().getFolhasMensalista(filter);
      setFolhasMensalista(response);
      setFolhasMensalistaLoading(false);
    } catch (errors) {
      setFolhasMensalistaLoading(false);
    }
  }, []);

  const getFolhasMensalistaLimit = useCallback(async (filter) => {
    try {
      setFolhasMensalistaLimiteLoading(true);
      const response = await api.folhaMensalista().getFolhasMensalista(filter);
      setFolhasMensalistaLimite(response);
      setFolhasMensalistaLimiteLoading(false);
    } catch (errors) {
      setFolhasMensalistaLimiteLoading(false);
    }
  }, []);

  const getFolhaMensalista = useCallback(async (id) => {
    try {
      setFolhaMensalistaLoading(true);
      const response = await api.folhaMensalista().getFolhaMensalista(id);
      setFolhaMensalista(response);
      setFolhaMensalistaLoading(false);
    } catch (errors) {
      setFolhaMensalistaLoading(false);
    }
  }, []);

  const insertFolha = useCallback(
    async (data) => {
      try {
        setInsertFolhaLoading(true);
        const {
          descricao,
          periodo,
          fluxo,
          data: date,
          categoria,
          preencherAtividades,
          regra,
          valorDia,
          valorExtra,
          incluiMaodeobra,
          incluiProducao,
          incluiColheita,
        } = data.sobre;
        const dataInicio = periodo?.substr(0, 10);
        const dataFim = periodo?.substr(13, 21);
        const selectedColabs = data.colaboradores.map(
          (colaborador) => colaborador.id,
        );

        const talhoesValidos = Array.isArray(data?.talhao?.talhoes)
          ? data.talhao.talhoes
              .filter((item) => item.id > 0)
              .map((item) => Number(item.id))
          : [];

        const fazendas = Array.isArray(data?.talhao?.talhoes)
          ? data.talhao.talhoes
              .filter((item) => item.id === 0 && item.gloFazendaId)
              .map((item) => Number(item.gloFazendaId))
          : [];

        const talhao = {
          filtrar_talhoes: data?.talhao?.filtrar_talhoes?.id || 0,
          talhoes: talhoesValidos,
          fazendas,
        };

        const newData = {
          descricao,
          inicio: dataInicio,
          colaboradores: selectedColabs,
          fim: dataFim,
          data: date,
          fluxo: fluxo?.id,
          categoria: categoria?.id,
          talhao,
          preencherAtividades,
          tipo: data?.sobre?.tipo || 'mensalista',
          regra: regra?.id,
          valorDia: valorDia ? toPriceInt(valorDia) : null,
          valorExtra: valorExtra ? toPriceInt(valorExtra) : null,
          incluiMaodeobra: incluiMaodeobra ?? true,
          incluiProducao: incluiProducao ?? true,
          incluiColheita: incluiColheita ?? true,
        };
        const response = await api.folhaMensalista().insertFolha(newData);
        setInsertFolhaLoading(false);
        showToast({
          type: 'success',
          message: 'Folha adicionada com sucesso!',
        });
        return response;
      } catch (err) {
        setInsertFolhaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const changeFolha = useCallback(
    async (id, data) => {
      try {
        setChangeFolhaLoading(true);

        const {
          descricao,
          periodo,
          fluxo,
          conta,
          pagamento,
          data: date,
          categoria,
          preencherAtividades,
          regra,
          valorDia,
          valorExtra,
          incluiMaodeobra,
          incluiProducao,
          incluiColheita,
        } = data.sobre;

        const dataInicio = periodo?.substr(0, 10);
        const dataFim = periodo?.substr(13, 21);
        const selectedColabs = data.colaboradores.map(
          (colaborador) => colaborador.id,
        );

        const talhoesValidos = Array.isArray(data?.talhao?.talhoes)
          ? data.talhao.talhoes
              .filter((item) => item.id > 0)
              .map((item) => Number(item.id))
          : [];

        const fazendas = Array.isArray(data?.talhao?.talhoes)
          ? data.talhao.talhoes
              .filter((item) => item.id === 0 && item.gloFazendaId)
              .map((item) => Number(item.gloFazendaId))
          : [];

        const talhao = {
          filtrar_talhoes: data?.talhao?.filtrar_talhoes?.id || 0,
          talhoes: talhoesValidos,
          fazendas,
        };

        const newData = {
          descricao,
          colaboradores: selectedColabs,
          inicio: dataInicio,
          fim: dataFim,
          data: date,
          fazenda: 13,
          conta: conta?.id,
          fluxo: fluxo?.id,
          categoria: categoria?.id,
          pagamento: pagamento?.id,
          talhao,
          preencherAtividades,
          regra: regra?.id,
          valorDia: valorDia ? toPriceInt(valorDia) : null,
          valorExtra: valorExtra ? toPriceInt(valorExtra) : null,
          incluiMaodeobra: incluiMaodeobra ?? true,
          incluiProducao: incluiProducao ?? true,
          incluiColheita: incluiColheita ?? true,
        };
        await api.folhaMensalista().editFolha(id, newData);
        getFolhaMensalista(id);
        showToast({
          type: 'success',
          message: 'Folha editada com sucesso!',
        });
        setChangeFolhaLoading(false);
      } catch (err) {
        setChangeFolhaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getFolhaMensalista],
  );

  const removeFolha = useCallback(
    async (id) => {
      try {
        setDeleteFolhaLoading(true);
        await api.folhaMensalista().removeFolha(id);
        setFolhasMensalista(folhasMensalista.filter((item) => item.id !== id));
        setDeleteFolhaLoading(false);
        showToast({
          type: 'success',
          message: 'Folha deletada com sucesso!',
        });
      } catch (err) {
        setDeleteFolhaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [folhasMensalista, showToast],
  );

  const getFolhaDetails = useCallback(async (id, filter) => {
    try {
      setFolhaDetailsLoading(true);
      const response = await api.folhaMensalista().getFolhaDetails(id, filter);
      setFolhaDetails(response);
      setFolhaDetailsLoading(false);
    } catch (errors) {
      setFolhaDetailsLoading(false);
    }
  }, []);

  const editFolhaDetail = useCallback(
    async (id, data, colaborador) => {
      try {
        setFolhaDetailsLoading(true);
        const { valor } = data;

        const newData = {
          valor: toPriceInt(valor),
        };
        await api.folhaMensalista().editFolhaDetail(id, newData, colaborador);
        getFolhaDetails(id);
        setFolhaDetailsLoading(false);
      } catch (err) {
        setFolhaDetailsLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getFolhaDetails],
  );

  const setPagamentoMensalista = useCallback(
    async (idFolha, idColaborador, data) => {
      try {
        setSetPagamentoMensalistaLoading(true);
        const { valor } = data;
        const newData = {
          valor: toPriceInt(valor),
        };
        await api
          .folhaMensalista()
          .setPagamentoMensalista(idFolha, idColaborador, newData);
        showToast({
          type: 'success',
          message: 'Valor reajustando com sucesso',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setSetPagamentoMensalistaLoading(false);
      }
    },
    [showToast],
  );

  const resetFolhaMensalista = useCallback(() => {
    setFolhaMensalista({});
  }, []);

  const resetFolhaMensalistaDetail = useCallback(() => {
    setFolhaDetails([]);
  }, []);

  const abrirFolhaMensalista = useCallback(
    async (id) => {
      try {
        setAbrirFolhaMensalistaLoading(true);
        await api.folhaMensalista().abrirFolhaMensalista(id);
        await getFolhaMensalista(id);
        await getFolhaDetails(id);
        showToast({
          type: 'success',
          message: 'Folha aberta com sucesso!',
        });
        setAbrirFolhaMensalistaLoading(false);
      } catch (err) {
        setAbrirFolhaMensalistaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getFolhaMensalista, getFolhaDetails],
  );

  const fecharFolhaMensalista = useCallback(
    async (id, data) => {
      try {
        setFecharFolhaMensalistaLoading(true);
        const { categoria, rateio, fazenda } = data;

        const newData = {
          categoria: categoria?.id,
          rateio: rateio?.id,
          fazenda: fazenda?.id ?? fazendaAtiva?.id ?? 0,
        };

        await api.folhaMensalista().fecharFolhaMensalista(id, newData);

        await getFolhaMensalista(id);
        await getFolhaDetails(id);

        showToast({
          type: 'success',
          message: 'Folha fechada com sucesso!',
        });
        setFecharFolhaMensalistaLoading(false);
        setEmployeesWithoutSalaryError({
          open: false,
          mensagem: '',
          colaboradores: [],
        });

        setTimeout(async () => {
          await getFolhaMensalista(id);
          await getFolhaDetails(id);
        }, 800);
      } catch (err) {
        setFecharFolhaMensalistaLoading(false);

        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          '';
        const isErroSemSalario =
          errorMessage.includes('não possui salário configurado') ||
          errorMessage.includes('não possuem salário configurado');

        if (isErroSemSalario && err?.response?.status === 400) {
          const errorData = err?.response?.data?.data;

          if (
            errorData?.colaboradoresIds &&
            errorData.colaboradoresIds.length > 0
          ) {
            const colaboradores = errorData.colaboradores || [];

            const colaboradoresFormatados = colaboradores.map((colab) => ({
              id: colab.id,
              nome: colab.nome || 'Colaborador',
              tipo: colab.tipo || '',
            }));

            setEmployeesWithoutSalaryError({
              open: true,
              mensagem: errorMessage,
              colaboradores: colaboradoresFormatados,
            });
          } else {
            const nomesColaboradores = extrairNomesColaboradores(errorMessage);
            const colaboradoresComIds = buscarColaboradoresPorNome(
              nomesColaboradores,
              folhaDetails,
            );

            setEmployeesWithoutSalaryError({
              open: true,
              mensagem: errorMessage,
              colaboradores: colaboradoresComIds,
            });
          }
        } else {
          showToast(appWarnings.Danger(err));
        }
        throw new Error(err);
      }
    },
    [
      showToast,
      getFolhaMensalista,
      getFolhaDetails,
      fazendaAtiva,
      folhaDetails,
    ],
  );

  const downloadFolhaMensalistaXlsx = useCallback(
    async (id, filters, description = '', periodo = '') => {
      try {
        setDownloadFolhaMensalistaXlsxLoading(true);
        const fileData = await api
          .folhaMensalista()
          .downloadFolhaMensalistaXlsx(id, filters);

        const actualBlob = fileData.data;
        const blobType =
          actualBlob.type ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const blob = new Blob([actualBlob], { type: blobType });

        const now = new Date();
        const formattedDate = format(now, 'dd-MM-yyyy-HH-mm-ss');

        const fileName = `FOLHA-PAGAMENTO-${description}-${periodo}-GERADO-EM-${formattedDate}.xlsx`;

        saveAs(blob, fileName);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDownloadFolhaMensalistaXlsxLoading(false);
      }
    },
    [showToast],
  );

  const downloadResumoXlsx = useCallback(
    async (folhaId, description = '', periodo = '') => {
      try {
        setDownloadResumoXlsxLoading(true);
        const fileData = await api
          .folhaMensalista()
          .downloadResumoXlsx(folhaId);

        const actualBlob = fileData.data;
        const blobType =
          actualBlob.type ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const blob = new Blob([actualBlob], { type: blobType });

        const now = new Date();
        const formattedDate = format(now, 'dd-MM-yyyy-HH-mm-ss');

        const fileName = `RESUMO-FOLHA-PAGAMENTO-${description}-${periodo}-GERADO-EM-${formattedDate}.xlsx`;

        saveAs(blob, fileName);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDownloadResumoXlsxLoading(false);
      }
    },
    [showToast],
  );

  const closeEmployeesWithoutSalaryError = useCallback(() => {
    setEmployeesWithoutSalaryError({
      open: false,
      mensagem: '',
      colaboradores: [],
    });
  }, []);

  const contextValue = React.useMemo(
    () => ({
      getFolhaMensalista,
      getFolhasMensalista,
      getFolhaDetails,
      editFolhaDetail,
      changeFolha,
      insertFolha,
      removeFolha,
      employeesWithoutSalaryError,
      closeEmployeesWithoutSalaryError,
      resetFolhaMensalista,
      resetFolhaMensalistaDetail,
      abrirFolhaMensalista,
      abrirFolhaMensalistaLoading,
      fecharFolhaMensalista,
      fecharFolhaMensalistaLoading,
      folhaMensalista,
      folhaDetails,
      folhasMensalista,
      folhaDetailsLoading,
      insertFolhaLoading,
      changeFolhaLoading,
      deleteFolhaLoading,
      folhasMensalistaLoading,
      folhaMensalistaLoading,
      pagamentoList,
      folhaList,
      setPagamentoMensalista,
      setPagamentoMensalistaLoading,
      getFolhasMensalistaLimit,
      folhasMensalistaLimite,
      folhasMensalistaLimiteLoading,
      downloadFolhaMensalistaXlsx,
      downloadFolhaMensalistaXlsxLoading,
      downloadResumoXlsx,
      downloadResumoXlsxLoading,
    }),
    [
      getFolhaMensalista,
      getFolhasMensalista,
      getFolhaDetails,
      editFolhaDetail,
      changeFolha,
      insertFolha,
      removeFolha,
      resetFolhaMensalista,
      resetFolhaMensalistaDetail,
      abrirFolhaMensalista,
      abrirFolhaMensalistaLoading,
      fecharFolhaMensalista,
      fecharFolhaMensalistaLoading,
      folhaMensalista,
      folhaDetails,
      folhasMensalista,
      folhaDetailsLoading,
      insertFolhaLoading,
      changeFolhaLoading,
      deleteFolhaLoading,
      folhasMensalistaLoading,
      folhaMensalistaLoading,
      setPagamentoMensalista,
      setPagamentoMensalistaLoading,
      getFolhasMensalistaLimit,
      folhasMensalistaLimite,
      folhasMensalistaLimiteLoading,
      downloadFolhaMensalistaXlsx,
      downloadFolhaMensalistaXlsxLoading,
      downloadResumoXlsx,
      downloadResumoXlsxLoading,
      employeesWithoutSalaryError,
      closeEmployeesWithoutSalaryError,
    ],
  );

  return (
    <FolhaMensalistaContext.Provider value={contextValue}>
      {children}
    </FolhaMensalistaContext.Provider>
  );
}

export function useFolhaMensalista() {
  const context = useContext(FolhaMensalistaContext);

  if (!context)
    throw new Error('useFolhaMensalista must be used within an AuthProvider');

  return context;
}
